import { useNavigate } from "react-router-dom";
import { PortalSidebar, type SidebarNavSection } from "@/shared/layout/PortalSidebar";
import { SidebarProfileCard } from "@/shared/layout/SidebarProfileCard";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { useQuery } from "@tanstack/react-query";
import { reviewQueueSummaryQuery } from "@/portals/admin/api/adminQueries";
import { MedSyncWordmark } from "@/shared/components/MedSyncWordmark";

export const navSections: SidebarNavSection[] = [
  {
    title: "Administration",
    items: [
      { path: "/admin",             label: "Dashboard",    icon: "lucide:layout-dashboard", end: true },
      { path: "/admin/users",       label: "Patients",     icon: "lucide:users"                      },
      { path: "/admin/clinicians",  label: "Clinicians",   icon: "lucide:stethoscope"                },
      { path: "/admin/review-queue", label: "Review Queue", icon: "lucide:inbox"                     },
      { path: "/admin/assigned-items", label: "Assigned Items", icon: "lucide:clipboard-check"        },
      { path: "/admin/audit-trail", label: "Audit Trail",  icon: "lucide:history"                    },
    ],
  },
];

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

function AdminProfileCard() {
  const admin = useAdminAuthStore((s) => s.user);
  return (
    <SidebarProfileCard
      avatarInitials={admin ? getInitials(admin.name) : "A"}
      name="Admin"
      showChevron={false}
    />
  );
}

// Unclaimed queue items (Module 6) show as a count on "Review Queue"; hidden at 0.
function withQueueBadge(sections: SidebarNavSection[], unclaimed: number): SidebarNavSection[] {
  if (unclaimed <= 0) return sections;
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => (item.path === "/admin/review-queue" ? { ...item, badge: unclaimed } : item)),
  }));
}

export function AdminSidebar() {
  const signOut = useAdminAuthStore((s) => s.signOut);
  // A failed read keeps the last known value rather than flashing the badge away.
  const unclaimed = useQuery(reviewQueueSummaryQuery()).data?.unclaimedCount ?? 0;
  const navigate = useNavigate();

  const handleSignOut = () => {
    signOut();
    navigate("/admin/signin", { replace: true });
  };

  return (
    <PortalSidebar
      logoTitle={<MedSyncWordmark />}
      logoSubtitle="Admin Portal"
      profileSlot={<AdminProfileCard />}
      navSections={withQueueBadge(navSections, unclaimed)}
      onSignOut={handleSignOut}
    />
  );
}
